import { useCallback } from 'react';
import { API } from 'aws-amplify';
import {
  CLONE_TEMPLATE,
  UPDATE_TEMPLATE_ITEMS,
  PUBLISH_TEMPLATE,
  UNPUBLISH_TEMPLATE,
  CREATE_NEW_TEMPLATE_VERSION,
  CREATE_FINDING_TYPE,
  UPDATE_FINDING_TYPE,
  DELETE_FINDING_TYPE,
  CREATE_ZONE,
  UPDATE_ZONE,
  DELETE_ZONE,
} from '../graphql/mutations';

async function executeMutation(
  mutation: any,
  fieldName: string,
  request: Record<string, any>
) {
  const result: any = await API.graphql({
    query: mutation as any,
    variables: {
      request: JSON.stringify(request),
    },
  });

  return JSON.parse(result.data[fieldName]);
}

export default function useTemplateMutations() {
  const orgID = (global as any)?.me?.orgID;
  const userID = (global as any)?.me?.id;

  // Template mutations
  const cloneTemplate = useCallback(
    async (sourceTemplateId: string) => {
      return executeMutation(CLONE_TEMPLATE, 'cloneTemplate', {
        orgId: orgID,
        sourceTemplateId,
        userId: userID,
      });
    },
    [orgID, userID]
  );

  const updateTemplateItems = useCallback(
    async (templateId: string, actions: any[]) => {
      return executeMutation(UPDATE_TEMPLATE_ITEMS, 'updateTemplateItems', {
        orgId: orgID,
        templateId,
        userId: userID,
        actions,
      });
    },
    [orgID, userID]
  );

  const publishTemplate = useCallback(
    async (templateId: string) => {
      return executeMutation(PUBLISH_TEMPLATE, 'publishTemplate', {
        orgId: orgID,
        templateId,
        userId: userID,
      });
    },
    [orgID, userID]
  );

  const unpublishTemplate = useCallback(
    async (templateId: string) => {
      return executeMutation(UNPUBLISH_TEMPLATE, 'unpublishTemplate', {
        orgId: orgID,
        templateId,
        userId: userID,
      });
    },
    [orgID, userID]
  );

  const createNewVersion = useCallback(
    async (templateId: string) => {
      return executeMutation(
        CREATE_NEW_TEMPLATE_VERSION,
        'createNewTemplateVersion',
        {
          orgId: orgID,
          templateId,
          userId: userID,
        }
      );
    },
    [orgID, userID]
  );

  // Finding type mutations
  const createFindingType = useCallback(
    async (data: {
      name: string;
      code: string;
      description?: string;
      category?: string;
      tasks?: any[];
    }) => {
      return executeMutation(CREATE_FINDING_TYPE, 'createFindingType', {
        orgId: orgID,
        userId: userID,
        ...data,
      });
    },
    [orgID, userID]
  );

  const updateFindingType = useCallback(
    async (findingTypeId: string, data: Record<string, any>) => {
      return executeMutation(UPDATE_FINDING_TYPE, 'updateFindingType', {
        orgId: orgID,
        findingTypeId,
        userId: userID,
        ...data,
      });
    },
    [orgID, userID]
  );

  const deleteFindingType = useCallback(
    async (findingTypeId: string) => {
      return executeMutation(DELETE_FINDING_TYPE, 'deleteFindingType', {
        orgId: orgID,
        findingTypeId,
        userId: userID,
      });
    },
    [orgID, userID]
  );

  // Zone mutations
  const createZone = useCallback(
    async (data: {
      name: string;
      code: string;
      description?: string;
      sortOrder?: number;
    }) => {
      return executeMutation(CREATE_ZONE, 'createZone', {
        orgId: orgID,
        userId: userID,
        ...data,
      });
    },
    [orgID, userID]
  );

  const updateZone = useCallback(
    async (zoneId: string, data: Record<string, any>) => {
      return executeMutation(UPDATE_ZONE, 'updateZone', {
        orgId: orgID,
        zoneId,
        userId: userID,
        ...data,
      });
    },
    [orgID, userID]
  );

  const deleteZone = useCallback(
    async (zoneId: string) => {
      return executeMutation(DELETE_ZONE, 'deleteZone', {
        orgId: orgID,
        zoneId,
        userId: userID,
      });
    },
    [orgID, userID]
  );

  return {
    cloneTemplate,
    updateTemplateItems,
    publishTemplate,
    unpublishTemplate,
    createNewVersion,
    createFindingType,
    updateFindingType,
    deleteFindingType,
    createZone,
    updateZone,
    deleteZone,
  };
}
